import React, { useState, useRef, useCallback, useEffect } from 'react';
import { usePdfApi } from '../../../../hooks/usePdfApi';
import PDFAnalysisResult from './PDFAnalysisResult';
import PDFAnalysisOptions from './PDFAnalysisOptions';

const PDFReader = () => {
  const { loading, error, result, analyzePDF } = usePdfApi();
  const [file, setFile] = useState(null);
  const [options, setOptions] = useState({
    summaryType: 'standard',
    analysisType: 'general'
  });
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    console.log('PDFReader 컴포넌트가 마운트됨');
  }, []);

  // 파일 선택 핸들러
  const handleFileUpload = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setProgress(20);
      try {
        await analyzePDF(selectedFile, options);
        setProgress(100);
      } catch (error) {
        console.error('PDF 분석 오류:', error);
        setProgress(0);
      }
    }
  };

  // 클릭으로 파일 선택
  const handleClick = () => {
    console.log('파일 선택 버튼 클릭됨');
    fileInputRef.current?.click();
  };

  // 드래그 앤 드롭 핸들러 추가
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) {
      console.log('드롭된 파일 없음');
      return;
    }

    console.log('파일 드롭됨:', droppedFile.name, droppedFile.type, droppedFile.size);
    
    try {
      setFile(droppedFile);
      const result = await analyzePDF(droppedFile, options);
      console.log('분석 결과:', result);
    } catch (error) {
      console.error('파일 처리 오류:', error);
    }
  }, [analyzePDF, options]);

  // 컴포넌트 렌더링 시마다 실행
  console.log('PDFReader 렌더링됨');

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">PDF 분석</h2>
      
      <PDFAnalysisOptions 
        options={options} 
        onOptionsChange={setOptions}
        disabled={loading} 
      />

      <div className={`relative ${isDragging ? 'ring-2 ring-blue-500' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileUpload}
          className="hidden"
          disabled={loading}
        />
        <div
          onClick={() => !loading && handleClick()}
          className={`
            cursor-pointer w-full p-6 border-2 border-dashed rounded-lg
            transition-all duration-200 ease-in-out
            ${isDragging 
              ? 'border-blue-500 bg-blue-50' 
              : loading 
                ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                : 'border-gray-300 hover:border-blue-500 hover:bg-gray-50'
            }
          `}
        >
          <div className="space-y-4 text-center">
            {loading ? (
              <>
                <div className="animate-pulse">
                  <p className="text-lg text-blue-600">PDF 분석 중...</p>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
                <p className="text-sm text-gray-500">잠시만 기다려주세요...</p>
              </>
            ) : (
              <>
                <div className="flex justify-center">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                  </svg>
                </div>
                <p className="text-lg text-gray-600">
                  {file 
                    ? `선택된 파일: ${file.name}`
                    : 'PDF 파일을 드래그하여 놓거나 클릭하여 선택하세요'
                  }
                </p>
                <p className="text-sm text-gray-500">
                  최대 파일 크기: 10MB
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 파일 선택 상태 표시 */}
      {file && !loading && !error && !result && (
        <div className="text-sm text-gray-600 mt-2">
          파일이 선택되었습니다. 분석을 시작합니다...
        </div>
      )}

      {/* 나머지 컴포넌트 코드는 동일 */}
      {loading && (
        <div className="mt-4 text-center text-blue-600">
          PDF 분석 중...
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"/>
          </svg>
          {error}
        </div>
      )}

      {result && (
        <div className="mt-4">
          <PDFAnalysisResult 
            result={result}
            fileName={file?.name}
          />
        </div>
      )}
    </div>
  );
};

export default PDFReader;
